import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Platform,
  Image,
  ScrollView,
} from 'react-native';
import Svg, { Path, G, ClipPath } from 'react-native-svg';

import { AddScopeControl } from './AddScopeControl';
import {
  Factory,
  MapPin,
  Server,
  Zap,
  Battery,
  Snowflake,
  Droplet,
  Laptop,
  Eye,
  FileText,
  Star,
  Building2,
  ClipboardList,
  ChevronRight,
  ChevronLeft,
  Package,
  Shield,
} from './icons';
import { ScopeStepOverflowMenu } from './ScopeStepOverflowMenu';
import {
  getRequestFormSidebarExpandedWidth,
  REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH,
  REQUEST_FORM_SIDEBAR_HEADER_CONTROL_HEIGHT,
} from '../constants/requestFormLayout';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useRequestScope } from '../contexts/RequestScopeContext';
import { ScopeMenuCoordinationProvider } from '../contexts/ScopeMenuCoordinationContext';
import { useSidebarStepMetrics } from '../contexts/SidebarStepMetricsContext';
import { IMPERSONATION_BANNER_HEIGHT } from './impersonation/ImpersonationBanner';
import { useCoolingSystemConfigurations } from '../hooks/queries/useCoolingSystemConfigurations';
import { useLargeUpsConfigurations } from '../hooks/queries/useLargeUpsConfigurations';
import { useLiquidCoolingCduConfigurations } from '../hooks/queries/useLiquidCoolingCduConfigurations';
import { usePowerDistributionConfigurations } from '../hooks/queries/usePowerDistributionConfigurations';
import { useRackConfigurations } from '../hooks/queries/useRackConfigurations';
import { useSmallUpsConfigurations } from '../hooks/queries/useSmallUpsConfigurations';
import { useEmbeddedSidebarFooterNavPadding } from '../hooks/useEmbeddedSidebarFooterNavPadding';
import { useStepVisibility } from '../hooks/useStepVisibility';
import { canReadDeliverablesAtStage } from '../utils/deliverablesVisibility';
import { partitionSidebarNavSteps } from '../utils/requestFormNavGroups';
import { shouldShowStepInSidebarNav } from '../utils/sidebarScopeStepVisibility';
import {
  formatRackUnitSubtitle,
  formatStepFieldProgressAccessibilityLabel,
  formatStepFieldProgressSubtitle,
  getDraftRackCountFromFormValues,
  getStepFieldProgress,
  isRepeatableUnitStep,
  REPEATABLE_UNIT_CONFIG_KEYS,
  resolveRepeatableUnitSubtitleCount,
} from '../utils/sidebarStepProgress';
import { formatSupportingDocumentsNavLabel } from '../utils/supportingDocumentsNavLabel';

import type { RequestFormStepId } from '../config/requestFormStepRegistry';

interface SidebarProps {
  activeItem?: string;
  onItemPress?: (item: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  navigate?: (path: string) => void;
  onStepChange?: (stepId: string) => void;
  showSummaryStep?: boolean;
  // Overlay mode props (for mobile)
  isVisible?: boolean;
  onClose?: () => void;
  overlayMode?: boolean;
  // Permission-based step filtering
  requestStage?: string;
  requestId?: string | null;
  usePermissionFiltering?: boolean;
  // IT Admin role for audit item visibility
  isITAdministrator?: boolean;
  topOffset?: number;
  embeddedInFrame?: boolean;
}

// Vertiv Logo Icon SVG Component
const VertivLogoIcon: React.FC<{ width?: number; height?: number }> = ({
  width = 32,
  height = 32,
}) => (
  <Svg viewBox="0 0 50 48" width={width} height={height}>
    <G clipPath="url(#cp1)">
      <ClipPath id="cp1">
        <Path d="m0 0.28h190v47h-190z" />
      </ClipPath>
      <Path
        d="m74.7 28.9l-5.3-14.9h-5.8l8.4 22.2h5.3l8.4-22.2h-5.6z"
        fill="#000"
      />
      <Path
        d="m89.4 36.2h16.6v-4.7h-11.4v-4.3h9.4v-4.5h-9.4v-4h11.4v-4.7h-16.6z"
        fill="#000"
      />
      <Path
        d="m24.9 23.6l9.7-9.6h12.6c-4.1-8.1-12.5-13.7-22.3-13.7-9.7 0-18.1 5.5-22.2 13.7h12.5l9.7 9.6z"
        fill="#000"
      />
      <Path
        d="m24.9 36.1l11.2 11.2c8.2-4.1 13.8-12.5 13.8-22.2 0-3.9-0.9-7.7-2.7-11.1z"
        fill="#000"
      />
      <Path
        d="m0 25.1c0 9.7 5.6 18.1 13.7 22.2l11.2-11.2-22.2-22.1c-1.8 3.4-2.7 7.2-2.7 11.1z"
        fill="#000"
      />
      <Path
        d="m129.3 21.5c0-4.2-3.4-7.5-7.5-7.5h-10.4v22.3h5.2v-7.2h2.4l4.9 7.2h6.1l-5.6-7.8c2.8-1 4.9-3.8 4.9-7zm-5.5 0.3c0 1.6-1.4 2.9-3 2.9h-4.3v-6h4.3c1.7 0 3 1.4 3 3q0 0.1 0 0.1z"
        fill="#000"
      />
      <Path d="m132.3 18.7h6.8v17.5h5.2v-17.5h6.8v-4.7h-18.8z" fill="#000" />
      <Path
        d="m189.4 32.8l-1.1 2.6-1-2.6h-0.7v3.4h0.6v-2.4l0.9 2.4h0.4l0.9-2.4v2.4h0.6v-3.4z"
        fill="#000"
      />
      <Path
        d="m175.3 28.9l-5.3-14.9h-5.8l8.4 22.2h5.3l8.4-22.2h-5.6z"
        fill="#000"
      />
      <Path d="m183.5 33.3h1.1v2.9h0.6v-2.9h1.1v-0.5h-2.8z" fill="#000" />
      <Path d="m160.6 14h-5.2v22.2h5.2z" fill="#000" />
    </G>
  </Svg>
);

/** Full nav order for Create Request (standalone Racks / Customer IT steps merged elsewhere). */
const ALL_SIDEBAR_FORM_STEPS = [
  {
    id: 'opportunity',
    icon: Factory,
    label: 'Opportunity',
    route: '/create-request',
    step: 1,
  },
  {
    id: 'site-information',
    icon: Building2,
    label: 'Site Information',
    route: '/create-request',
    step: 2,
  },
  {
    id: 'facility-power',
    icon: Zap,
    label: 'Facility Power Readiness',
    route: '/facility-power',
    step: 4,
  },
  {
    id: 'large-ups',
    icon: Battery,
    label: 'Large UPS',
    route: '/large-ups',
    step: 4.5,
  },
  {
    id: 'power-distribution',
    icon: Zap,
    label: 'Power Distribution',
    route: '/power-distribution',
    step: 4.6,
  },
  {
    id: 'rack-power',
    icon: Server,
    label: 'Racks',
    route: '/rack-power',
    step: 5,
  },
  {
    id: 'small-ups',
    icon: Battery,
    label: 'Small UPS',
    route: '/small-ups',
    step: 7,
  },
  {
    id: 'cooling-readiness',
    icon: Snowflake,
    label: 'Cooling Readiness',
    route: '/cooling-readiness',
    step: 8,
  },
  {
    id: 'liquid-cooling',
    icon: Droplet,
    label: 'Liquid Cooling',
    route: '/liquid-cooling',
    step: 9,
  },
  {
    id: 'customer-it',
    icon: Laptop,
    label: 'Customer IT Equipment',
    route: '/customer-it',
    step: 10,
  },
  {
    id: 'monitoring',
    icon: Eye,
    label: 'Monitoring Management',
    route: '/monitoring',
    step: 11,
  },
  {
    id: 'documents',
    icon: FileText,
    label: 'Supporting Documents',
    route: '/documents',
    step: 12,
  },
  {
    id: 'feedback',
    icon: Star,
    label: 'Feedback',
    route: '/feedback',
    step: 13,
  },
  {
    id: 'summary',
    icon: ClipboardList,
    label: 'Summary',
    route: '/summary',
    step: 14,
  },
] as const;

type SidebarFormStep = (typeof ALL_SIDEBAR_FORM_STEPS)[number] & {
  step: number;
};

function buildSidebarFormSteps(): SidebarFormStep[] {
  const list = ALL_SIDEBAR_FORM_STEPS.filter(s => {
    if (s.id === 'customer-it') return false;
    if (s.id === 'feedback') return false;
    if (s.id === 'monitoring') return false;
    return true;
  });
  return list.map((s, i) => {
    const step = { ...s, step: i + 1 };
    if (step.id === 'cooling-readiness') {
      return { ...step, label: 'Cooling' as const };
    }
    return step;
  }) as SidebarFormStep[];
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeItem = 'opportunity',
  onItemPress,
  isCollapsed = false,
  onToggleCollapse,
  navigate,
  onStepChange,
  showSummaryStep = true,
  isVisible: _isVisible = true,
  onClose: _onClose,
  overlayMode = false,
  requestStage,
  requestId,
  usePermissionFiltering = true,
  isITAdministrator = false,
  topOffset = 0,
  embeddedInFrame = false,
}) => {
  const isWeb = Platform.OS === 'web';
  const { isImpersonating } = useImpersonation();
  const bannerOffset = isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0;

  // (Removed unused animation state for overlay mode)
  // Show labels on web when not collapsed, or on mobile when in overlay mode (not collapsed)
  const shouldShowLabels = (isWeb && !isCollapsed) || (!isWeb && !isCollapsed);
  const sidebarWidth = shouldShowLabels
    ? getRequestFormSidebarExpandedWidth(isWeb)
    : REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH;

  // Get step visibility based on permissions
  // Pass requestId to ensure proper cache invalidation when switching between requests
  const { stepVisibility, isLoading: isLoadingPermissions } = useStepVisibility(
    usePermissionFiltering ? requestStage : undefined,
    usePermissionFiltering ? requestId || undefined : undefined,
  );

  const {
    rackUnitCount: sessionRackCount,
    supportingDocumentCount,
    formValues,
    showFullDetails,
    requestStage: metricsRequestStage,
    requestId: metricsRequestId,
  } = useSidebarStepMetrics();
  const { user } = useAuth();

  /**
   * Read access to the deliverables section, on the same terms the step filtering below uses:
   * the stage is only consulted when `usePermissionFiltering` is on, because off the detail
   * route it is stale. With filtering off this stays `true`, which preserves the item on
   * Create Request, where there is no stage and nothing to hide yet.
   */
  const canSeeDeliverables =
    !usePermissionFiltering ||
    canReadDeliverablesAtStage({
      userRoles: user?.roles,
      requestStage,
      requestId,
      userEmail: user?.email,
    });

  const fieldVisibility = React.useMemo(
    () => ({
      showFullDetails,
      requestStage:
        metricsRequestStage ??
        String(
          formValues?.sysRequestStage ??
            formValues?.stage ??
            requestStage ??
            '',
        ),
      requestId: metricsRequestId ?? requestId ?? undefined,
      userRoles: user?.roles,
      userEmail: user?.email,
    }),
    [
      showFullDetails,
      metricsRequestStage,
      metricsRequestId,
      formValues?.sysRequestStage,
      formValues?.stage,
      requestStage,
      requestId,
      user?.roles,
      user?.email,
    ],
  );
  const persistedRackRequestId =
    requestId && requestId !== 'new-request' ? requestId : undefined;
  const { data: persistedRacks } = useRackConfigurations(
    persistedRackRequestId ?? '',
    Boolean(persistedRackRequestId),
  );
  const { data: persistedSmallUps } = useSmallUpsConfigurations(
    persistedRackRequestId ?? '',
    Boolean(persistedRackRequestId),
  );
  const { data: persistedLargeUps } = useLargeUpsConfigurations(
    persistedRackRequestId ?? '',
    Boolean(persistedRackRequestId),
  );
  const { data: persistedPowerDistribution } =
    usePowerDistributionConfigurations(
      persistedRackRequestId ?? '',
      Boolean(persistedRackRequestId),
    );
  const { data: persistedCoolingSystems } = useCoolingSystemConfigurations(
    persistedRackRequestId ?? '',
    Boolean(persistedRackRequestId),
  );
  const { data: persistedLiquidCoolingCdus } =
    useLiquidCoolingCduConfigurations(
      persistedRackRequestId ?? '',
      Boolean(persistedRackRequestId),
    );

  const rackPowerSubtitleCount = React.useMemo(() => {
    // For a saved request the persisted rack query is authoritative and is
    // invalidated when racks are saved, so the count updates without a full
    // page refresh. The form/session snapshot can go stale because rack CRUD
    // goes through the rack API, not the request form state (#995).
    if (persistedRackRequestId) {
      return persistedRacks?.length ?? 0;
    }
    const draftFromForm = getDraftRackCountFromFormValues(formValues);
    if (draftFromForm !== undefined) {
      return draftFromForm;
    }
    if (sessionRackCount !== undefined) {
      return sessionRackCount;
    }
    return 0;
  }, [
    formValues,
    sessionRackCount,
    persistedRackRequestId,
    persistedRacks?.length,
  ]);

  const requestScope = useRequestScope();

  const repeatableUnitSubtitleCounts = React.useMemo(() => {
    const counts: Record<string, number> = {
      'rack-power': rackPowerSubtitleCount,
    };

    const persistedCountsByStep: Record<string, number | undefined> = {
      'large-ups': persistedLargeUps?.length,
      'power-distribution': persistedPowerDistribution?.length,
      'small-ups': persistedSmallUps?.length,
      'cooling-readiness': persistedCoolingSystems?.length,
      'liquid-cooling': persistedLiquidCoolingCdus?.length,
    };

    for (const stepId of Object.keys(REPEATABLE_UNIT_CONFIG_KEYS)) {
      if (stepId === 'rack-power') {
        continue;
      }

      counts[stepId] = resolveRepeatableUnitSubtitleCount(stepId, {
        persistedChildTableCount: persistedRackRequestId
          ? persistedCountsByStep[stepId]
          : undefined,
        formValues,
      });
    }

    return counts;
  }, [
    rackPowerSubtitleCount,
    persistedRackRequestId,
    persistedLargeUps?.length,
    persistedPowerDistribution?.length,
    persistedSmallUps?.length,
    persistedCoolingSystems?.length,
    persistedLiquidCoolingCdus?.length,
    formValues,
  ]);

  const getStepSubtitle = React.useCallback(
    (stepId: string): { text: string; accessibilityLabel: string } | null => {
      if (isRepeatableUnitStep(stepId)) {
        const count = repeatableUnitSubtitleCounts[stepId] ?? 0;
        const text = formatRackUnitSubtitle(count);
        return { text, accessibilityLabel: text };
      }
      const progress = getStepFieldProgress(stepId, formValues ?? {}, {
        enabledStepIds: requestScope?.enabledStepIds,
        storedStepIds: requestScope?.storedStepIds,
        stage: String(formValues?.sysRequestStage ?? formValues?.stage ?? ''),
        visibility: fieldVisibility,
      });
      if (!progress) {
        return null;
      }
      return {
        text: formatStepFieldProgressSubtitle(progress.filled, progress.total),
        accessibilityLabel: formatStepFieldProgressAccessibilityLabel(
          progress.filled,
          progress.total,
        ),
      };
    },
    [
      formValues,
      repeatableUnitSubtitleCounts,
      requestScope?.enabledStepIds,
      requestScope?.storedStepIds,
      fieldVisibility,
    ],
  );

  /**
   * Supporting Documents carries its attachment total in the label (#1534).
   *
   * The count is pushed in by `RequestDetail` rather than fetched here — see
   * `SidebarStepMetricsContext.supportingDocumentCount` for why. Every other
   * step keeps its registry label untouched.
   */
  const getStepNavLabel = React.useCallback(
    (step: SidebarFormStep): string =>
      step.id === 'documents'
        ? formatSupportingDocumentsNavLabel(step.label, supportingDocumentCount)
        : step.label,
    [supportingDocumentCount],
  );

  const stopSidebarActivation = React.useCallback(
    (event?: { stopPropagation?: () => void }) => {
      event?.stopPropagation?.();
    },
    [],
  );

  const formSteps = React.useMemo(() => {
    const base = buildSidebarFormSteps();
    if (!requestScope || !onStepChange) {
      return base;
    }
    const enabled = new Set(requestScope.enabledStepIds);
    return base.filter(step => enabled.has(step.id));
  }, [requestScope, onStepChange]);

  // Filter steps based on showSummaryStep prop and permissions
  let visibleSteps = showSummaryStep
    ? formSteps
    : formSteps.filter(step => step.id !== 'summary');

  // Apply permission filtering if enabled (#997: user-added optional scopes bypass RBAC nav hide)
  if (usePermissionFiltering && !isLoadingPermissions) {
    visibleSteps = visibleSteps.filter(step =>
      shouldShowStepInSidebarNav(step.id, {
        stepVisibility,
        enabledStepIds: requestScope?.enabledStepIds ?? [],
        storedStepIds: requestScope?.storedStepIds,
      }),
    );
  }

  const { primarySteps, footerSteps } = React.useMemo(
    () => partitionSidebarNavSteps(visibleSteps),
    [visibleSteps],
  );

  const footerNavPaddingBottom =
    useEmbeddedSidebarFooterNavPadding(embeddedInFrame);
  const footerNavStyle = React.useMemo(
    () =>
      footerNavPaddingBottom != null
        ? { paddingBottom: footerNavPaddingBottom }
        : undefined,
    [footerNavPaddingBottom],
  );

  // Web collapsed: icon rail when in create/detail flow; expanded + native keep full nav.
  const showStepNavigation = !isWeb || !isCollapsed || Boolean(onStepChange);

  const renderNavStep = (step: SidebarFormStep) => {
    const isRemovableScope =
      Boolean(requestScope?.showScopeControls) &&
      (requestScope?.removableStepIds.includes(step.id as RequestFormStepId) ??
        false);

    return (
      <View
        key={step.id}
        pointerEvents="box-none"
        style={[
          styles.sidebarItem,
          isRemovableScope && styles.sidebarItemWithScopeMenu,
          activeItem === step.id && styles.sidebarItemActive,
          !shouldShowLabels && styles.sidebarItemCollapsed,
        ]}
      >
        <Pressable
          testID={`wizard-section-${step.id}`}
          aria-selected={activeItem === step.id}
          style={({ pressed }) => [
            styles.sidebarItemMain,
            isRemovableScope && styles.sidebarItemMainWithScopeMenu,
            pressed && styles.sidebarItemMainPressed,
          ]}
          onPress={() => {
            if (onStepChange) {
              onStepChange(step.id);
            } else if (navigate) {
              navigate(step.route);
            }
            onItemPress?.(step.id);
          }}
        >
          <View style={styles.sidebarIconContainer}>
            <step.icon
              size={16}
              color={activeItem === step.id ? '#e55a2b' : '#666'}
            />
          </View>
          {shouldShowLabels && (
            <View style={styles.sidebarLabelBlock}>
              <Text
                style={[
                  styles.sidebarLabel,
                  activeItem === step.id && styles.sidebarLabelActive,
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {getStepNavLabel(step)}
              </Text>
              {(() => {
                const subtitle = getStepSubtitle(step.id);
                if (!subtitle) {
                  return null;
                }
                return (
                  <Text
                    style={styles.sidebarSubtitle}
                    testID={`sidebar-${step.id}-subtitle`}
                    accessibilityLabel={subtitle.accessibilityLabel}
                  >
                    {subtitle.text}
                  </Text>
                );
              })()}
            </View>
          )}
        </Pressable>
        {shouldShowLabels && isRemovableScope ? (
          <View style={styles.scopeMenuSlot}>
            <ScopeStepOverflowMenu
              stepId={step.id as RequestFormStepId}
              stepLabel={step.label}
              stopSidebarActivation={stopSidebarActivation}
            />
          </View>
        ) : null}
      </View>
    );
  };

  /**
   * Deliverables nav item — available wherever a request form is (FR11): a brand-new unsaved
   * request, a saved Draft, In Progress, and Closed (read-only there, enforced by the step
   * rather than by hiding the way in).
   *
   * Hardcoded rather than registered as a step because `buildRequestFormSteps` and
   * `buildDetailFormSteps` are the same function over the same registry, so a registered step
   * would also surface in the wizard footer navigation and pick up scope semantics.
   *
   * The gate now reads `requestStage`, but only when `usePermissionFiltering` is on, which is
   * the same condition `useStepVisibility` above already applies to it. That was the original
   * objection and it still stands: off the detail route the stage is app-level state left over
   * from a previously-open request, so it is stale-prone and must not be trusted there.
   *
   * The rest of that comment was wrong and is why this item leaked. It claimed "there is no
   * longer any stage this item is hidden on"; the DRAFT matrix marks Deliverables `-` for
   * eleven of fourteen roles, and every one of them could see this item and open the section
   * behind it. (MR !489 review, batch 2 change 2.3.)
   */
  const deliverablesNavItem =
    onStepChange && requestId && canSeeDeliverables ? (
      <TouchableOpacity
        key="deliverables"
        testID="sidebar-deliverables-item"
        style={[
          styles.sidebarItem,
          activeItem === 'deliverables' && styles.sidebarItemActive,
          !shouldShowLabels && styles.sidebarItemCollapsed,
        ]}
        onPress={() => {
          onStepChange('deliverables');
          onItemPress?.('deliverables');
        }}
      >
        {/* Inner structure mirrors `renderNavStep` exactly. The label must sit inside
            `sidebarLabelBlock`, whose `marginLeft: 12` is the entire icon-to-label gap;
            rendering the Text directly after the icon container leaves it flush against
            the icon and visibly out of step with every neighbouring item. */}
        <View style={styles.sidebarItemMain}>
          <View style={styles.sidebarIconContainer}>
            <Package
              size={16}
              color={activeItem === 'deliverables' ? '#e55a2b' : '#666'}
            />
          </View>
          {shouldShowLabels && (
            <View style={styles.sidebarLabelBlock}>
              <Text
                style={[
                  styles.sidebarLabel,
                  activeItem === 'deliverables' && styles.sidebarLabelActive,
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                Deliverables
              </Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    ) : null;

  // Deliverables sits inside the pinned footer group, immediately above Summary, rather than
  // in a slot of its own below it. If Summary is not among the visible footer steps it falls
  // to the end of the group, and if there are no footer steps at all the group renders for
  // Deliverables alone.
  const footerNavNodes: React.ReactNode[] = [];
  footerSteps.forEach(step => {
    if (step.id === 'summary' && deliverablesNavItem) {
      footerNavNodes.push(deliverablesNavItem);
    }
    footerNavNodes.push(renderNavStep(step));
  });
  if (deliverablesNavItem && !footerSteps.some(step => step.id === 'summary')) {
    footerNavNodes.push(deliverablesNavItem);
  }

  return (
    <ScopeMenuCoordinationProvider>
      <View
        testID="request-form-sidebar"
        style={[
          styles.sidebar,
          { width: sidebarWidth },
          isWeb && styles.sidebarColumn,
          isWeb && !embeddedInFrame && !overlayMode && styles.sidebarWeb,
          isWeb &&
            !embeddedInFrame &&
            !overlayMode && {
              top: bannerOffset + topOffset,
              height: `calc(100vh - ${bannerOffset + topOffset}px)` as any,
            },
          isWeb && (embeddedInFrame || overlayMode) && styles.sidebarEmbedded,
          // Desktop embeds the sidebar in a flex row, so flexBasis sizes its width.
          isWeb &&
            embeddedInFrame &&
            !overlayMode && {
              width: sidebarWidth,
              minWidth: sidebarWidth,
              maxWidth: sidebarWidth,
              flexBasis: sidebarWidth as any,
            },
          // The overlay drawer is a flex column, where flexBasis would size HEIGHT
          // instead — a width there collapses the nav to a few clipped pixels (#1153).
          // SidebarOverlay passes embeddedInFrame too, so overlayMode must win.
          isWeb && overlayMode && styles.sidebarOverlayWeb,
          isWeb &&
            overlayMode && {
              width: sidebarWidth,
              minWidth: sidebarWidth,
              maxWidth: sidebarWidth,
            },
          !isWeb && overlayMode && styles.sidebarNativeOverlay,
        ]}
      >
        {/* Header: + Add Scope (left) and collapse nav (right) — Chapman #997 */}
        <View
          style={[
            styles.sidebarHeader,
            !(onStepChange && shouldShowLabels) &&
              styles.sidebarHeaderCollapseOnly,
            !isWeb && overlayMode && styles.sidebarHeaderNativeOverlay,
          ]}
        >
          {onStepChange && shouldShowLabels ? (
            <View style={styles.addScopeHeaderSlot}>
              <AddScopeControl
                stepVisibility={
                  usePermissionFiltering ? stepVisibility : undefined
                }
              />
            </View>
          ) : null}
          {(_onClose || onToggleCollapse) && (
            <TouchableOpacity
              style={styles.collapseButton}
              onPress={() => {
                if (_onClose) {
                  _onClose();
                  return;
                }
                onToggleCollapse?.();
              }}
              testID="sidebar-collapse-button"
              accessibilityRole="button"
              accessibilityLabel={
                _onClose || overlayMode
                  ? 'Minimize navigation'
                  : 'Toggle navigation'
              }
            >
              {_onClose || overlayMode || !isCollapsed ? (
                <ChevronLeft size={18} color="#666" />
              ) : (
                <ChevronRight size={18} color="#666" />
              )}
            </TouchableOpacity>
          )}
        </View>

        {/* Navigation: primary steps scroll; footer steps pinned above sidebar bottom */}
        {showStepNavigation ? (
          <View style={styles.sidebarNavBody}>
            <ScrollView
              style={[
                styles.sidebarContent,
                !isWeb && styles.sidebarContentNative,
              ]}
              contentContainerStyle={[
                styles.sidebarPrimaryContent,
                primarySteps.length === 0 && styles.sidebarPrimaryContentEmpty,
              ]}
              showsVerticalScrollIndicator={true}
              testID="wizard-sidebar"
            >
              {visibleSteps.length === 0 && (
                <Text style={styles.emptyStepsMessage}>No steps available</Text>
              )}
              {primarySteps.map(renderNavStep)}
            </ScrollView>
            {footerNavNodes.length > 0 ? (
              <View style={[styles.navFooterPinned, footerNavStyle]}>
                <View
                  style={styles.navFooterDivider}
                  testID="sidebar-nav-footer-divider"
                />
                {footerNavNodes}
              </View>
            ) : null}
            {/* Impersonation Audit item - IT Admin only, RequestDetail only */}
            {isITAdministrator &&
              onStepChange &&
              requestId &&
              requestId !== 'new-request' && (
                <View style={[styles.auditFooterSlot, footerNavStyle]}>
                  <View style={styles.auditDivider} />
                  <TouchableOpacity
                    style={[
                      styles.sidebarItem,
                      activeItem === 'impersonation-audit' &&
                        styles.sidebarItemActive,
                      !shouldShowLabels && styles.sidebarItemCollapsed,
                    ]}
                    onPress={() => {
                      onStepChange('impersonation-audit');
                      onItemPress?.('impersonation-audit');
                    }}
                  >
                    <View style={styles.sidebarIconContainer}>
                      <Shield
                        size={16}
                        color={
                          activeItem === 'impersonation-audit'
                            ? '#e55a2b'
                            : '#666'
                        }
                      />
                    </View>
                    {shouldShowLabels && (
                      <Text
                        style={[
                          styles.sidebarLabel,
                          activeItem === 'impersonation-audit' &&
                            styles.sidebarLabelActive,
                        ]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        Impersonation Audit
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              )}
          </View>
        ) : null}
      </View>
    </ScopeMenuCoordinationProvider>
  );
};

const styles = StyleSheet.create({
  sidebar: {
    flex: 1,
    backgroundColor: '#fff',
  },
  sidebarColumn: {
    flexDirection: 'column',
    minHeight: 0,
    ...Platform.select({
      web: {
        display: 'flex' as const,
        height: '100%' as any,
      },
    }),
  },
  sidebarNativeOverlay: {
    overflow: 'visible' as const,
  },
  sidebarWeb: {
    position: 'fixed' as any,
    left: 0,
    top: 0,
    height: '100vh' as any,
    zIndex: 100,
    backgroundColor: '#fff',
    borderRightWidth: 1,
    borderRightColor: '#e9ecef',
    overflow: 'hidden' as any,
  },
  sidebarEmbedded: {
    flexGrow: 0,
    flexShrink: 0,
    height: '100%' as any,
    overflow: 'hidden' as any,
    flexDirection: 'column',
    ...Platform.select({
      web: {
        display: 'flex' as const,
        minHeight: 0,
      },
    }),
  },
  sidebarOverlayWeb: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto' as any,
    height: '100%' as any,
  },
  sidebarHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    width: '100%',
    padding: 14,
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    ...Platform.select({
      web: {
        flexShrink: 0,
        overflow: 'visible' as const,
        zIndex: 30,
      },
      ios: {
        flexShrink: 0,
        overflow: 'visible' as const,
        zIndex: 30,
      },
    }),
  },
  sidebarHeaderNativeOverlay: {
    paddingTop: 16,
    alignItems: 'flex-start',
  },
  sidebarHeaderCollapseOnly: {
    justifyContent: 'center',
  },
  addScopeHeaderSlot: {
    flex: 1,
    minWidth: 0,
    alignSelf: 'stretch',
    ...Platform.select({
      web: {
        overflow: 'visible' as const,
        zIndex: 30,
      },
      ios: {
        overflow: 'visible' as const,
        zIndex: 30,
      },
    }),
  },
  sidebarTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      default: {
        width: '100%',
        paddingHorizontal: 10,
      },
    }),
  },
  logo: {
    width: 120,
    height: 38,
  },
  logoImage: {
    width: 120,
    height: 38,
    ...Platform.select({
      default: {
        maxWidth: '100%',
        resizeMode: 'contain' as any,
      },
    }),
  },
  collapseButton: {
    width: REQUEST_FORM_SIDEBAR_HEADER_CONTROL_HEIGHT,
    height: REQUEST_FORM_SIDEBAR_HEADER_CONTROL_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e1e5ea',
    borderRadius: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
      default: {},
    }),
  },
  sidebarContent: {
    flex: 1,
    width: '100%',
    minHeight: 0,
    ...Platform.select({
      web: {
        overflowY: 'auto' as const,
      },
    }),
  },
  sidebarNavBody: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    flexDirection: 'column',
    ...Platform.select({
      web: {
        display: 'flex' as const,
        overflow: 'hidden' as const,
      },
    }),
  },
  sidebarPrimaryContent: {
    paddingHorizontal: 10,
    paddingBottom: 8,
    flexGrow: 1,
    ...Platform.select({
      web: {
        overflow: 'visible' as const,
      },
    }),
  },
  sidebarPrimaryContentEmpty: {
    flexGrow: 0,
  },
  emptyStepsMessage: {
    color: '#666',
    padding: 20,
    textAlign: 'center',
  },
  sidebarContentNative: {
    paddingHorizontal: 5,
  },
  sidebarItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 4,
    paddingLeft: 12,
    paddingVertical: 10,
    marginVertical: 2,
    borderRadius: 6,
    minHeight: 40,
    position: 'relative',
    ...Platform.select({
      web: {
        overflow: 'visible' as const,
      },
    }),
  },
  sidebarItemWithScopeMenu: {
    paddingRight: 0,
  },
  scopeMenuSlot: {
    flexShrink: 0,
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    ...Platform.select({
      web: {
        pointerEvents: 'auto' as const,
        zIndex: 100,
      },
    }),
  },
  sidebarItemMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    minWidth: 0,
    paddingRight: 4,
    paddingTop: 2,
    ...Platform.select({
      web: {
        zIndex: 1,
      },
    }),
  },
  sidebarItemMainWithScopeMenu: {
    paddingRight: 8,
  },
  sidebarItemMainPressed: {
    opacity: 0.85,
  },
  sidebarItemCollapsed: {
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  sidebarItemActive: {
    backgroundColor: '#fff3f0',
  },
  sidebarIconContainer: {
    width: 20,
    alignItems: 'center',
    paddingTop: 2,
  },
  sidebarLabelBlock: {
    marginLeft: 12,
    flex: 1,
    minWidth: 0,
  },
  sidebarLabel: {
    fontSize: 14,
    color: '#666',
  },
  sidebarSubtitle: {
    fontSize: 11,
    color: '#999',
    marginTop: 2,
  },
  sidebarLabelActive: {
    color: '#e55a2b',
    fontWeight: '500',
  },
  stepNumber: {
    fontSize: 10,
    color: '#666',
    fontWeight: '600',
    textAlign: 'center',
  },
  stepNumberActive: {
    color: '#ff6b35',
  },
  auditDivider: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    marginTop: 8,
    paddingTop: 8,
  },
  navFooterPinned: {
    flexShrink: 0,
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 24,
    width: '100%',
  },
  navFooterDivider: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    marginBottom: 8,
    marginHorizontal: 4,
  },
  auditFooterSlot: {
    flexShrink: 0,
    paddingHorizontal: 10,
    paddingBottom: 16,
  },
});
